import { gql } from '@apollo/client';

/**
 * Saved screens — a filtered universe someone kept.
 *
 * A screen is a set of filters and weights run over one kind of security, and
 * the row carries what it found the last time it ran. `screenType` says which
 * universe it screens: schemes, stocks, ETFs or models.
 *
 * One document, two readings, because the field answers both from the same
 * shape:
 *   - `screenType` (or neither argument) lists the caller's SAVED screens;
 *   - `urlId` returns the ONE screen behind a screen-builder handle, which is
 *     not saved at all — the builder writes its result set to Redis under
 *     `bolodhan:c_s_b:<urlId>` with an hour's TTL. An unknown or expired
 *     handle answers with an empty list, never an error.
 * When `urlId` is given the resolver ignores `screenType` (the asset class
 * rides inside the stored payload), so callers pass one or the other.
 *
 * `getScreenerMutualFunds` does NOT read the builder's namespace — it reads
 * `bolodhan:mf_screener:<urlId>`, which only the `screen_mutual_funds` tool
 * writes. The two handles are indistinguishable UUIDs and the agent sends
 * both, which is why `useScreenerFunds` has to try this one too.
 *
 * `metrics` on a result row is the asset class's own columns as field / value
 * pairs (a stock screen: sector, return_1y, sharpe_ratio, volatility,
 * pe_ratio, return_on_equity, market_cap_percentile; the model's ideas:
 * percentile, alpha_bp, sector, top_factors). The Screener tab draws them.
 */
export const GET_MY_CUSTOM_SCREENS = gql`
  query GetMyCustomScreens($screenType: ScreenTypeEnum, $urlId: String) {
    getMyCustomScreens(screenType: $screenType, urlId: $urlId) {
      id
      name
      screenType
      intentSummary
      createdAt
      updatedAt
      lastRunAt
      filters { field value }
      weights { field weight }
      results { symbol name assetClass rank score metrics { field value } }
    }
  }
`;
